import { Autocomplete, Box, InputAdornment, PopperProps, TextField } from "@mui/material";
import { JSXElementConstructor, createElement } from "react";
import { Controller, FieldValues, Path, get, useFormContext, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons";

import { CommonStatusItem } from "@/types/global";

import CommonTooltip from "./CommonTooltip";

export type CommonSelectorProps<FV extends FieldValues, O> = {
    name: Path<FV>;
    getOptionLabel?: (val: O | null) => string;
    renderOption: JSXElementConstructor<{ option: O; selected?: boolean }>;
    icon: IconType;
    options: O[];
    multiple?: boolean;
    placeholder: string;
    startAdornment?: JSX.Element;
    PopperComponent?: React.ComponentType<PopperProps>;
    disableCloseOnSelect?: boolean;
    value?: O[];
    onChange?: (value: (O | null)[] | NonNullable<O> | null) => void;
};

function CommonSelector<FV extends FieldValues, O>({
    name,
    getOptionLabel,
    renderOption,
    icon,
    options,
    multiple = false,
    placeholder,
    startAdornment,
    PopperComponent,
    disableCloseOnSelect,
    value,
    onChange,
}: CommonSelectorProps<FV, O>) {
    const { t } = useTranslation();
    const { control } = useFormContext<FV>();
    const { errors } = useFormState<FV>();
    const fieldError = get(errors, name);

    return (
        <Controller<FV>
            name={name}
            control={control}
            render={({ field }) => (
                <Autocomplete
                    {...field}
                    multiple={multiple}
                    PopperComponent={PopperComponent}
                    disableCloseOnSelect={disableCloseOnSelect}
                    classes={{
                        inputRoot: " !h-[47px]",
                    }}
                    sx={() => ({
                        "label + &": {
                            marginTop: 20,
                        },
                        "& .MuiInputBase-input": {
                            paddingBottom: 12,
                            paddingTop: 12,
                        },
                    })}
                    value={value ? value : field.value || null}
                    options={options}
                    onChange={(_, value) => {
                        if (onChange) onChange(value || null);
                        else field.onChange(value || null);
                    }}
                    isOptionEqualToValue={(option, value) => option === value}
                    getOptionLabel={getOptionLabel}
                    renderOption={(props, value, { selected }) => {
                        const { key, ...optionProps } = props;
                        return (
                            value && (
                                <Box
                                    key={key}
                                    component="li"
                                    {...optionProps}
                                    sx={{
                                        padding: 0,
                                        gap: 0,
                                    }}
                                >
                                    {createElement(renderOption, { option: value, selected })}
                                </Box>
                            )
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={t("placeholders.select", { field: t(placeholder) })}
                            variant="filled"
                            sx={{
                                "& .MuiInputBase-input": {
                                    transform: multiple ? "translateY(-12px)" : "translateY(-8px)",
                                },
                            }}
                            error={!!fieldError}
                            helperText={(fieldError && fieldError?.message) || ""}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: startAdornment || (
                                    <CommonDefaultStartAdornment<FV, O> multiple={multiple} icon={icon} />
                                ),
                            }}
                        />
                    )}
                />
            )}
        />
    );
}
function CommonDefaultStartAdornment<T extends FieldValues, O>({
    multiple,
    icon,
}: Pick<CommonSelectorProps<T, O>, "multiple" | "icon">) {
    return (
        <InputAdornment
            position="end"
            className={`!-translate-x-1  ${multiple ? "!-translate-y-3" : "!-translate-y-2"}`}
        >
            {createElement(icon)}
        </InputAdornment>
    );
}

export function CommonSelectorItem<T>({
    option,
    options,
    prefix,
}: {
    option: T | null;
    options: (CommonStatusItem & { value: T })[];
    prefix: string;
}) {
    const { t } = useTranslation();
    const item = option && options.find((item) => item.value === option);
    if (!item) return null;
    return (
        <>
            <div className="flex justify-between items-center w-full ">
                <div className="flex items-center gap-2 ">
                    {createElement(item.component(), { color: item.text })}
                    {t(prefix + item.value)}
                </div>
                <CommonTooltip tooltip={item} />
            </div>
        </>
    );
}

export default CommonSelector;
